"use client";

import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { Plus } from "lucide-react";
import DataFreshness, { type PageStoreChip } from "./DataFreshness";
import type { DataCoverage } from "@/lib/data-coverage";
import { flowCoverColor } from "@/lib/flow-cover-key";

const PAGE_ICONS = ["📦", "🗒️", "📈", "📊", "💡", "📌", "🧾", "🛒", "🚚", "💰", "📣", "🧮", "🗓️", "✅", "⭐", "🔥", "👟", "👕", "🏷️", "⚙️", "🧠", "🤝", "🐸", "🎯"] as const;
const noop = () => {};

/** Same header in the persistent page frame and standalone editor routes.
 * Persistence stays with FlowEditorDoc. A verified pending header may show
 * metadata, but has no store actions or editable callbacks. */
export type WorkspaceDocumentHeaderProps = {
  pageId: string; title: string; icon: string; description: string;
  canEdit: boolean; pending?: boolean; metadataReady?: boolean; cover?: string;
  /** Lightweight list card, not permission to edit or run data requests. */
  previewReady?: boolean; storePending?: boolean;
  changedAt?: number; coverage?: DataCoverage | null;
  storeChip?: PageStoreChip | null;
  titleRef?: Ref<HTMLInputElement>; editorReady?: boolean;
  onIconChange?: (icon: string) => void;
  onTitleChange?: (title: string) => void;
  onDescriptionChange?: (description: string) => void;
  onAddFirstBlock?: () => void;
  coverControls?: ReactNode; notices?: ReactNode;
};

function IconPicker({ icon, onPick, disabled }: { icon: string; onPick: (i: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Ширина полосы прокрутки страницы — в переменную для дорожки таблицы.
  // Дорожка задана через 100vw, а он включает полосу прокрутки: без поправки
  // таблица вылезает за правый край и страница начинает ездить по горизонтали.
  useEffect(() => {
    const apply = () => {
      const sbw = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.setProperty("--frog-sbw", `${sbw}px`);
    };
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" disabled={disabled} onClick={() => setOpen(v => !v)} title="Сменить иконку"
        className="-m-1 block rounded-lg p-1 text-[40px] leading-none transition-colors hover:bg-black/5">{icon}</button>
      {open && !disabled && (
        <div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-xl border border-[#e4dfd3] bg-white p-2 shadow-lg">
          <div className="grid grid-cols-8 gap-0.5">
            {PAGE_ICONS.map(e => (
              <button key={e} type="button" onClick={() => { onPick(e); setOpen(false); }}
                className="flex h-7 w-7 items-center justify-center rounded-md text-[17px] transition-colors hover:bg-gray-100">{e}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export default function WorkspaceDocumentHeader({ pageId, title, icon, description, canEdit, pending, metadataReady, previewReady, storePending,
  cover, changedAt, coverage, storeChip, titleRef, editorReady, onIconChange, onTitleChange,
  onDescriptionChange, onAddFirstBlock, coverControls, notices,
}: WorkspaceDocumentHeaderProps) {
  const fieldsReady = !pending || metadataReady || previewReady;
  const coverColor = flowCoverColor(cover);
  return <div data-workspace-document-header className="group/hdr pl-[68px] sm:pl-[72px]">
    <div className="relative">
      {coverColor && <div data-page-color-cover aria-hidden="true" style={{ backgroundColor: coverColor }} className="h-32 w-full rounded-2xl" />}
      {/* Keep the controls at the same React position when switching modes:
          choosing a color must not close/remount the floating picker. */}
      <div className={coverColor ? "absolute right-3 top-3" : "relative -mt-1 mb-1 h-7"}>
        {coverControls}
      </div>
      <div data-page-icon className={coverColor ? "relative z-10 -mt-6 ml-4 inline-flex rounded-xl bg-[#f7f5f0] p-2 shadow-[0_1px_3px_rgba(55,53,47,0.04)]" : "inline-flex"}>
        <IconPicker key={pageId} icon={icon} disabled={!canEdit} onPick={onIconChange ?? noop} />
      </div>
    </div>
    {/* «+» слева от заголовка (виден при наведении) — создаёт первый блок
        под заголовком, как гуттерный «+» в Notion, но для позиции над
        всем содержимым редактора. */}
    <div className="group/title relative mt-2">
      {canEdit && editorReady && (
        <button
          type="button"
          onClick={onAddFirstBlock}
          title="Добавить блок под заголовком"
          aria-label="Добавить блок под заголовком"
          className="absolute -left-9 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-[#b3ab98] opacity-0 transition-all hover:bg-white hover:text-[#37352f] hover:shadow-[0_3px_10px_-3px_rgba(60,50,30,0.3)] hover:ring-1 hover:ring-[#e6e0d2] group-hover/title:opacity-100"
        >
          <Plus size={18} />
        </button>
      )}
      <input
        ref={titleRef}
        value={title}
        onChange={e => onTitleChange?.(e.target.value)}
        readOnly={!canEdit} aria-busy={pending || undefined}
        placeholder="Без названия"
        /* Размеры из макета-эталона: 38/700 с плотным межбуквенным.
           Название задаёт тон заголовкам внутри документа, поэтому оно
           заметно крупнее их и стоит отдельно от текста. */
        className="flow-head-field block w-full bg-transparent text-[38px] font-bold leading-[1.15] tracking-[-0.03em] text-[#37352f] outline-none placeholder:text-gray-300"
      />
    </div>
    {/* Описание — «зачем эта страница». Через месяц автор сам не помнит,
        для чего заводил «План закупки», а коллега по ссылке не понимает
        этого никогда. Поле видно всегда бледной строкой: пустая подсказка
        тут дешевле, чем спрятанный в меню пункт, который никто не найдёт. */}
    <div className="relative mt-1.5">
      <input
        value={description}
        onChange={e => onDescriptionChange?.(e.target.value)}
        readOnly={!canEdit}
        placeholder={canEdit ? "Добавить описание…" : ""}
        maxLength={300}
        aria-label="Описание страницы"
        className="flow-head-field block w-full max-w-[640px] bg-transparent text-[14.5px] leading-[1.45] text-[#787774] outline-none placeholder:text-[#cbc6ba]"
      />
      {!fieldsReady && <div data-workspace-description-skeleton aria-hidden className="pointer-events-none absolute inset-0 flex items-center">
        <div className="ui-skeleton h-3.5 w-[min(320px,70%)] rounded" />
      </div>}
    </div>
    {/* Служебная строка — последней в шапке: когда правили страницу и
        по какое число есть цифры. Мелкая и тихая: её ищут, когда она
        понадобилась, а не читают каждый раз. */}
    <div className="mt-2 min-h-[23px]">{!fieldsReady ?
      <div data-workspace-properties-skeleton aria-hidden className="flex min-h-[23px] flex-wrap items-center gap-2">
        <div className="ui-skeleton h-[23px] w-[90px] rounded-md" />
        <div className="ui-skeleton h-2.5 w-[110px] rounded" />
        <div className="ui-skeleton h-2.5 w-[125px] rounded" />
      </div> : <div className="flex flex-wrap items-center gap-2">
        {pending && !metadataReady && storePending && <div data-workspace-store-skeleton aria-hidden className="ui-skeleton h-[23px] w-[90px] rounded-md" />}
        <DataFreshness coverage={coverage} changedAt={changedAt} store={storeChip} className="" />
      </div>}</div>

    {notices}
  </div>;
}
